import { constant, isEmpty, times } from 'lodash';
import { FC, ReactElement } from 'react';
import { useToggle } from 'react-use';
import clsx from 'clsx';

import { BaseBlock } from '@/components/base-block';
import { Icon } from '@/components/icon';
import { Link } from '@/components/link';
import type { Width } from '@/lib/types';
import { Feature } from '@/components/comparison-table/feature';

const ANALYTICS_CONTEXT = 'CTA - Table';

export type ProductFeature = {
  type: string;
  title: string;
  subtitle?: string;
  description?: string;
  category?: string;
  link?: {
    url?: string;
    label?: string;
  };
};

type FeatureValue = {
  type: string;
  isIncluded: boolean;
  conditions?: string;
};

export type Product = {
  title: string;
  icon?: string;
  label?: string;
  description?: string;
  price?: {
    monthly?: string;
    annual?: string;
    single?: string;
    hasAnnualVehicleSuffix?: boolean;
  };
  url?: string;
  isFeatured: boolean;
  featuredLabel?: string;
  features: Array<FeatureValue>;
};

type ComparisonTableProps = {
  features: Array<ProductFeature>;
  products: Array<Product>;
  limit?: number;
  headerHeight?: number;
  terms?: ReactElement;
  width?: Width;
  anchor?: string | null;
};

const ComparisonTable: FC<ComparisonTableProps> = ({
  products,
  features,
  limit = 14,
  headerHeight = 8,
  terms = null,
  width = 'Narrow',
  anchor = null,
}) => {
  const [showAllFeatures, toggleShowAllFeatures] = useToggle(false);
  const initialFeatures = features.slice(0, limit);
  const otherFeatures = features.filter((feature) => !feature.category && !initialFeatures.includes(feature));
  const categorizedFeatures = features
    .filter((feature) => feature.category)
    .reduce<{ category: string; items: ProductFeature[] }[]>((acc, feature) => {
      const category = feature.category as string;
      const existingCategory = acc.find((item) => item.category === category);

      if (existingCategory) {
        existingCategory.items.push(feature);
      } else {
        acc.push({ category, items: [feature] });
      }

      return acc;
    }, []);
  const hasInitialFeatures = !isEmpty(initialFeatures);
  const hasOtherFeatures = !isEmpty(otherFeatures);
  const hasSomeProductPricing = products.some((product) => Boolean(product.price));
  const hasSomeProductLinks = products.some((product) => Boolean(product.url));
  const hasSomeProductDescription = products.some((product) => Boolean(product.description));

  return (
    <BaseBlock className="comparison-block" width={width} anchor={anchor} hasContentPadding={false}>
      <table className={clsx('comparison-table', `item-count-${products.length}`)}>
        <thead>
          <tr className="comparison-recommended-product-indicator" aria-hidden>
            <td className="ct-label-cell" />
            {products.map((product) => {
              const featuredLabel = product.featuredLabel || 'Most popular';

              return (
                <td key={product.title} className={clsx({ 'is-recommended': product.isFeatured })}>
                  {product.isFeatured ? featuredLabel : null}
                </td>
              );
            })}
          </tr>

          <tr className="comparison-product-titles">
            <td className="ct-label-cell" />
            {products.map((product) => {
              const title = (
                <span className="comparison-product-title h5 p-0 p-sm-3 p-md-2 text-reset">{product.title}</span>
              );
              return (
                <th
                  key={product.title}
                  className={clsx('p-0 p-sm-4', {
                    'is-recommended': product.isFeatured,
                  })}
                  data-mobile-height={headerHeight}
                >
                  {product.icon && <Icon className="fa-2x d-none d-md-inline-block text-reset" name={product.icon} />}

                  {product.url ? (
                    <Link
                      href={product.url}
                      target="_blank"
                      className="thlink"
                      analytics={{
                        context: ANALYTICS_CONTEXT,
                        text: product.title,
                      }}
                    >
                      {title}
                    </Link>
                  ) : (
                    <span className="thlink">{title}</span>
                  )}
                </th>
              );
            })}
          </tr>

          {hasSomeProductPricing && (
            <tr className="comparison-product-prices">
              <td className="ct-label-cell" />
              {products.map((product) => {
                const hasMonthlyPrice = Boolean(product?.price?.monthly);
                const hasAnnualPrice = Boolean(product?.price?.annual);
                const hasSinglePrice = Boolean(product?.price?.single);
                const contents = (
                  <>
                    {hasSinglePrice && <span className="comparison-product-price h6">${product?.price?.single}</span>}
                    {hasMonthlyPrice && (
                      <>
                        <span className="comparison-product-price h3">${product?.price?.monthly}</span>
                        <span className="comparison-product-price-units moabbr h6">/month</span>
                      </>
                    )}
                    {hasAnnualPrice && (
                      <>
                        <span className="comparison-product-price-units h6">
                          <br />
                          or ${product?.price?.annual}
                        </span>
                        <span className="comparison-product-price-units h6">/yr</span>
                      </>
                    )}
                    {product?.price?.hasAnnualVehicleSuffix && (
                      <>
                        <span className="comparison-product-price-units moabbr h6">/yr</span>
                        <span className="comparison-product-price-units h6">
                          <br />
                          per vehicle
                        </span>
                      </>
                    )}
                  </>
                );
                return (
                  <td key={product.title} className={clsx({ 'is-recommended': product.isFeatured })}>
                    {product.url ? (
                      <Link
                        href={product.url}
                        target="_blank"
                        className="thlink tablelink2"
                        analytics={{
                          context: ANALYTICS_CONTEXT,
                          text: product.title,
                        }}
                      >
                        {contents}
                      </Link>
                    ) : (
                      contents
                    )}
                  </td>
                );
              })}
            </tr>
          )}

          {hasSomeProductDescription && (
            <tr className="comparison-product-descriptions d-none d-md-table-row">
              <td className="ct-label-cell" />
              {products.map((product) => {
                const description = (
                  <span className="comparison-product-description body-5">{product.description}</span>
                );
                return (
                  <td key={product.title} className={clsx({ 'is-recommended': product.isFeatured })}>
                    {product?.description && (
                      <>
                        {product.url ? (
                          <Link
                            href={product.url}
                            target="_blank"
                            className="thlink"
                            analytics={{
                              context: ANALYTICS_CONTEXT,
                              text: product.title,
                            }}
                          >
                            {description}
                          </Link>
                        ) : (
                          description
                        )}
                      </>
                    )}
                  </td>
                );
              })}
            </tr>
          )}
        </thead>

        <tbody>
          {hasInitialFeatures &&
            initialFeatures.map((feature) => <Feature key={feature.type} feature={feature} products={products} />)}

          {(hasOtherFeatures || categorizedFeatures.length > 0) && (
            <>
              {showAllFeatures ? (
                <>
                  {otherFeatures.map((feature) => (
                    <Feature key={feature.type} feature={feature} products={products} />
                  ))}

                  {categorizedFeatures.map((category) => (
                    <>
                      <tr className="comparison-table-teaser">
                        <th colSpan={products.length + 1}>
                          <span className="comparison-product-title h5 p-0 p-sm-3 p-md-2 text-reset">
                            {category.category}
                          </span>
                        </th>
                      </tr>

                      {category.items.map((item) => (
                        <Feature key={item.type} feature={item} products={products} />
                      ))}
                    </>
                  ))}
                </>
              ) : (
                <tr className="comparison-table-teaser">
                  <td className="ct-label-cell" />
                  {times(products.length, constant(<td className="filler-cell" />))}
                </tr>
              )}
              <tr className="comparison-table-show-extra">
                <td className="ct-label-cell" />
                <td colSpan={products.length + 1}>
                  <button
                    type="button"
                    className="btn btn-sm btn-link comparison-table-toggle-all-features"
                    onClick={() => toggleShowAllFeatures()}
                  >
                    <Icon className="mr-2" name={showAllFeatures ? 'minus-circle' : 'plus-circle'} />
                    {showAllFeatures ? 'Show fewer details' : ' Show all details'}
                  </button>
                </td>
              </tr>
            </>
          )}
        </tbody>

        {hasSomeProductLinks && (
          <tfoot>
            <tr>
              <td className="ct-label-cell" />
              {products.map((product) => {
                if (!product.url) {
                  return null;
                }

                return (
                  <td key={product.title} className={clsx({ 'is-recommended': product.isFeatured })}>
                    <Link
                      href={product.url}
                      className={clsx('btn btn-sm btn-block', `btn-${product.isFeatured ? 'goal' : 'primary'}`)}
                      target="_blank"
                      analytics={{
                        context: ANALYTICS_CONTEXT,
                        text: product.title,
                      }}
                    >
                      <span>Get {product.title}</span>
                    </Link>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        )}
      </table>

      {terms && <div className="mt-3 comparison-table-terms">{terms}</div>}
    </BaseBlock>
  );
};

export { ComparisonTable };
